import React, { useState, useEffect } from 'react';
import { Checkbox, Space } from 'antd';
import ConfirmationModal from '../../CustomComponents/ConfirmationModal';
import {
  ColumnSettingsContainer,
  ColumnCheckboxItem,
  ResetButton
} from '../JobList/styledComponents';

const JOB_COLUMNS = [
  { key: 'jobNumber', label: 'Job Number', mandatory: true },
  { key: 'author', label: 'Author', mandatory: false },
  { key: 'patientName', label: 'Patient Name', mandatory: false },
  { key: 'submissionTime', label: 'Submission Time', mandatory: false },
  { key: 'effectiveDate', label: 'Encounter Date', mandatory: false },
  { key: 'billStatus', label: 'Bill Status', mandatory: false },
  { key: 'mipsScore', label: 'MIPS Score', mandatory: false },
  { key: 'facilityName', label: 'Facility', mandatory: false },
  { key: 'status', label: 'Status', mandatory: false },
  { key: 'workType', label: 'Work Type', mandatory: false },
  { key: 'dictationTemplateName', label: 'Template', mandatory: false },
  { key: 'assignedBiller', label: 'Assigned Biller', mandatory: false },
  { key: 'billerName', label: 'Claimed Biller', mandatory: false },
  { key: 'reviewerName', label: 'Current Reviewer', mandatory: false },
  { key: 'scribeName', label: 'Scribe', mandatory: false },
  { key: 'currentLevelOfReview', label: 'Current Review Level', mandatory: false },
  { key: 'submissionToReviewerLevel1Time', label: 'Sent to Reviewer', mandatory: false },
  { key: 'rejectedByReviewerTime', label: 'Reviewer Rejection Time', mandatory: false },
  { key: 'billedTime', label: 'Billed Time', mandatory: false },
  { key: 'emrSubmissionTime', label: 'EMR Submission Time', mandatory: false },
  { key: 'cptCodesInContent', label: 'CPT Code', mandatory: false },
  { key: 'admissionDate', label: 'Admission Date', mandatory: false },
  { key: 'mrn', label: 'MRN', mandatory: false },
  { key: 'dischargeDate', label: 'Discharge Date', mandatory: false },
  { key: 'primaryCarePhysician', label: 'Primary Care Physician', mandatory: false }
];

const USER_COLUMNS = [
  { key: 'fullName', label: 'Name', mandatory: true },
  { key: 'email', label: 'Email', mandatory: false },
  { key: 'userType', label: 'User Type', mandatory: false },
  { key: 'loginTime', label: 'Login', mandatory: false },
  { key: 'loggedUserIp', label: 'IP Address', mandatory: false },
  { key: 'loggingStatus', label: 'Status', mandatory: false },
  { key: 'logoutTime', label: 'Logout', mandatory: false }
];

export const DEFAULT_JOB_VISIBLE_COLUMNS = {
  jobNumber: true,
  author: true,
  patientName: true,
  submissionTime: true,
  effectiveDate: false,
  facilityName: true,
  status: true,
  workType: true,
  dictationTemplateName: false,
  assignedBiller: false,
  billerName: false,
  billStatus: false,
  mipsScore: true,
  reviewerName: false,
  scribeName: false,
  currentLevelOfReview: false,
  submissionToReviewerLevel1Time: false,
  rejectedByReviewerTime: false,
  billedTime: false,
  emrSubmissionTime: false,
  cptCodesInContent: false,
  admissionDate: false,
  mrn: false,
  dischargeDate: false,
  primaryCarePhysician: false
};

export const DEFAULT_USER_VISIBLE_COLUMNS = {
  profilePic: true,
  fullName: true,
  email: true,
  userType: true,
  loginTime: true,
  loggedUserIp: true,
  loggingStatus: true,
  logoutTime: true
};

const AuditColumnSettingsModal = ({
  visible,
  onCancel,
  onSave,
  visibleColumns,
  selectedTab,
  excludeColumns = []
}) => {
  const [tempColumns, setTempColumns] = useState(visibleColumns);

  useEffect(() => {
    setTempColumns(visibleColumns);
  }, [visibleColumns]);

  const columnConfig = (selectedTab === 1 ? JOB_COLUMNS : USER_COLUMNS).filter(
    col => !excludeColumns.includes(col.key)
  );

  const handleToggle = key => {
    const column = columnConfig.find(c => c.key === key);
    if (!column?.mandatory) {
      setTempColumns({ ...tempColumns, [key]: !tempColumns[key] });
    }
  };

  const handleCancel = () => {
    setTempColumns(visibleColumns);
    onCancel();
  };

  const handleSave = () => {
    onSave(tempColumns);
    onCancel();
  };

  const handleReset = () => {
    const resetDefaults = {
      jobNumber: true,
      author: true,
      patientName: true,
      submissionTime: true,
      effectiveDate: true,
      facilityName: false,
      status: false,
      workType: false,
      dictationTemplateName: false,
      assignedBiller: false,
      billerName: false,
      billStatus: true,
      reviewerName: false,
      scribeName: false,
      currentLevelOfReview: false,
      submissionToReviewerLevel1Time: false,
      rejectedByReviewerTime: false,
      billedTime: false,
      emrSubmissionTime: false,
      cptCodesInContent: false,
      admissionDate: false,
      mrn: false,
      dischargeDate: false,
      primaryCarePhysician: false
    };
    excludeColumns.forEach(key => delete resetDefaults[key]);
    setTempColumns(resetDefaults);
  };

  return (
    <ConfirmationModal
      visible={visible}
      onCancel={handleCancel}
      onOk={handleSave}
      header='Customize Columns'
      confirmation={true}
      antdModalProps={{ centered: true, width: 450, closable: false }}
    >
      {/* Kept above the scrollable list so it's reachable without scrolling */}
      {selectedTab === 1 && (
        <ResetButton onClick={handleReset}>Reset to Default</ResetButton>
      )}
      <ColumnSettingsContainer>
        <Space direction='vertical' size='middle' style={{ width: '100%' }}>
          {columnConfig.map(column => (
            <ColumnCheckboxItem key={column.key}>
              <Checkbox
                checked={tempColumns[column.key]}
                onChange={() => handleToggle(column.key)}
                disabled={column.mandatory}
              >
                {column.label}
                {column.mandatory && (
                  <span style={{ color: '#999' }}> (Required)</span>
                )}
              </Checkbox>
            </ColumnCheckboxItem>
          ))}
        </Space>
      </ColumnSettingsContainer>
    </ConfirmationModal>
  );
};

export default AuditColumnSettingsModal;
